//src/admin/AdminHome.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AdminLanguageContext } from "./context/AdminLanguageContext";
import {
  FaBox,
  FaShoppingCart,
  FaUsers,
  FaDollarSign,
  FaGlobe,
  FaWhatsapp,
  FaStore,
  FaFilter,
} from "react-icons/fa";
import { getAdminDashboardStats } from "../services/adminDashboard.api";

function AdminHome() {
  const { lang } = useContext(AdminLanguageContext);

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";

const canAccess = isAdmin || perms.can_view_analytics;


  // draft filters (UI)
  const [draftSource, setDraftSource] = useState("all");
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // applied filters (API)
  const [filters, setFilters] = useState({
    source: "all",
    from: "",
    to: "",
  });

  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
  orders: 0,
  sales: 0,
  users: 0,
  products: 0,

  // ✅ NEW
  wholesale_orders: 0,
  wholesale_sales: 0,
});

  // ✅ default fetch on open (ALL + ALL dates)
  useEffect(() => {
  if (!canAccess) return; // ✅ STOP HERE IF NO PERMISSION

  setLoading(true);
  getAdminDashboardStats(filters)
    .then((data) => setStats(data))
    .catch(() => {}) // 🔇 silence forbidden noise
    .finally(() => setLoading(false));
}, [filters, canAccess]);


  const TEXT = {
    EN: {
      overview: "Overview",
      orders: "Total Orders",
      sales: "Total Sales",
      users: "Users",
      products: "Products",
      from: "From",
      to: "To",
      apply: "Apply",
      filters: "Filters",
      all: "All",
      website: "Website",
      whatsapp: "WhatsApp",
      store: "Store",
      reset: "Reset",
      showing: "Showing",
      allDates: "All dates",
      wholesaleOrders: "Wholesale Orders",
wholesaleSales: "Wholesale Sales",
    },
    AR: {
      overview: "نظرة عامة",
      orders: "إجمالي الطلبات",
      sales: "إجمالي المبيعات",
      users: "المستخدمين",
      products: "المنتجات",
      from: "من",
      to: "إلى",
      apply: "تطبيق",
      filters: "الفلاتر",
      all: "الكل",
      website: "الموقع",
      whatsapp: "واتساب",
      store: "المتجر",
      reset: "إعادة ضبط",
      showing: "عرض",
      allDates: "كل التواريخ",
      wholesaleOrders: "طلبات الجملة",
wholesaleSales: "مبيعات الجملة",

    },
  }[lang];

  const applyFilters = () => {
    setFilters({
      source: draftSource,
      from: draftFrom,
      to: draftTo,
    });
  };

  const resetFilters = () => {
    // reset draft UI
    setDraftSource("all");
    setDraftFrom("");
    setDraftTo("");

    // reset applied filters (this triggers API refresh)
    setFilters({
      source: "all",
      from: "",
      to: "",
    });
  };

  const FilterButton = ({ value, icon, label }) => {
    const active = draftSource === value;
    return (
      <button
        type="button"
        onClick={() => setDraftSource(value)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition
          ${
            active
              ? "bg-cyan-600 text-white border-cyan-600 shadow"
              : "bg-white hover:bg-gray-50 text-gray-700"
          }
        `}
      >
        {icon}
        {label}
      </button>
    );
  };

  // ✅ Active filter summary (based on APPLIED filters)
  const activeSummary = useMemo(() => {
    const sourceLabel =
      filters.source === "all"
        ? TEXT.all
        : filters.source === "website"
        ? TEXT.website
        : filters.source === "whatsapp"
        ? TEXT.whatsapp
        : TEXT.store;

    const fromText = filters.from ? filters.from : null;
    const toText = filters.to ? filters.to : null;

    const datePart =
      !fromText && !toText
        ? TEXT.allDates
        : fromText && toText
        ? `${fromText} → ${toText}`
        : fromText
        ? `${TEXT.from}: ${fromText}`
        : `${TEXT.to}: ${toText}`;

    return `${sourceLabel} · ${datePart}`;
  }, [filters, TEXT]);

  useEffect(() => {
  setFilters({
    source: draftSource,
    from: draftFrom,
    to: draftTo,
  });
}, [draftSource, draftFrom, draftTo]);


 return (
  <div>
    {!canAccess ? (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 font-semibold">
          {lang === "AR"
            ? "غير مصرح لك بالوصول إلى لوحة التحكم"
            : "You are not allowed to access the dashboard"}
        </p>
      </div>
    ) : (
      <>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
          {TEXT.overview}
        </h1>

        {/* FILTERS */}
        <div className="mb-6">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="w-full bg-white border rounded-2xl shadow-sm px-5 py-4 flex items-center justify-between font-bold text-gray-700 hover:bg-gray-50 transition"
          >
            <div className="flex items-center gap-2">
              <FaFilter />
              {TEXT.filters}
            </div>
            <span
              className={`transition-transform ${
                filtersOpen ? "rotate-180" : ""
              }`}
            >
              ▼
            </span>
          </button>

        {/* FILTER CONTENT */}
        {filtersOpen && (
          <div className="bg-white border border-t-0 rounded-b-2xl shadow-sm p-4 sm:p-5 animate-fadeSlide -mt-1">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-6">
              {/* SOURCE BUTTONS */}
              <div className="flex flex-wrap gap-2">
                <FilterButton value="all" icon={<FaGlobe />} label={TEXT.all} />
                <FilterButton
                  value="website"
                  icon={<FaShoppingCart />}
                  label={TEXT.website}
                />
                <FilterButton
                  value="whatsapp"
                  icon={<FaWhatsapp />}
                  label={TEXT.whatsapp}
                />
                <FilterButton
                  value="store"
                  icon={<FaStore />}
                  label={TEXT.store}
                />
              </div>

              {/* DATE RANGE */}
              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-600 mb-1">
                    {TEXT.from}
                  </label>
                  <input
                    type="date"
                    value={draftFrom}
                    onChange={(e) => setDraftFrom(e.target.value)}
                    className="border rounded-xl px-3 py-2 focus:ring-2 focus:ring-cyan-400 outline-none"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-600 mb-1">
                    {TEXT.to}
                  </label>
                  <input
                    type="date"
                    value={draftTo}
                    onChange={(e) => setDraftTo(e.target.value)}
                    className="border rounded-xl px-3 py-2 focus:ring-2 focus:ring-cyan-400 outline-none"
                  />
                </div>
              </div>

              {/* APPLY */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="
                    px-5 py-2.5 rounded-xl bg-cyan-600 text-white font-bold
                    shadow hover:bg-cyan-700 transition w-full lg:w-auto
                  "
                >
                  {TEXT.apply}
                </button>

                {/* ✅ RESET (inside dropdown too, optional but useful) */}
                <button
                  type="button"
                  onClick={resetFilters}
                  className="
                    px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold
                    hover:bg-gray-200 transition w-full lg:w-auto
                  "
                >
                  {TEXT.reset}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white shadow rounded-xl p-6 flex items-center gap-4">
            <FaShoppingCart className="text-4xl text-cyan-600" />
            <div>
              <h3 className="font-semibold">{TEXT.orders}</h3>
              <p className="text-2xl font-bold">
                {loading ? "..." : stats.orders}
              </p>
            </div>
          </div>

          <div className="bg-white shadow rounded-xl p-6 flex items-center gap-4">
            <FaDollarSign className="text-4xl text-green-500" />
            <div>
              <h3 className="font-semibold">{TEXT.sales}</h3>
              <p className="text-2xl font-bold">
                {loading ? "..." : `$${stats.sales}`}
              </p>
            </div>
          </div>

          <div className="bg-white shadow rounded-xl p-6 flex items-center gap-4">
            <FaUsers className="text-4xl text-purple-600" />
            <div>
              <h3 className="font-semibold">{TEXT.users}</h3>
              <p className="text-2xl font-bold">
                {loading ? "..." : stats.users}
              </p>
            </div>
          </div>

          <div className="bg-white shadow rounded-xl p-6 flex items-center gap-4">
            <FaBox className="text-4xl text-orange-500" />
            <div>
              <h3 className="font-semibold">{TEXT.products}</h3>
              <p className="text-2xl font-bold">
                {loading ? "..." : stats.products}
              </p>
            </div>
          </div>
</div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
  {/* WHOLESALE ORDERS */}
  <div className="bg-white shadow rounded-xl p-6 flex items-center gap-4 border-l-4 border-indigo-500">
    <FaStore className="text-4xl text-indigo-600" />
    <div>
      <h3 className="font-semibold">{TEXT.wholesaleOrders}</h3>
      <p className="text-2xl font-bold">
        {loading ? "..." : stats.wholesale_orders}
      </p>
    </div>
  </div>

  {/* WHOLESALE SALES */}
  <div className="bg-white shadow rounded-xl p-6 flex items-center gap-4 border-l-4 border-emerald-500">
    <FaDollarSign className="text-4xl text-emerald-600" />
    <div>
      <h3 className="font-semibold">{TEXT.wholesaleSales}</h3>
      <p className="text-2xl font-bold">
        {loading ? "..." : `$${stats.wholesale_sales}`}
      </p>
    </div>
  </div>

        </div>

        {/* ACTIVE FILTER */}
        <div className="mt-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border text-sm text-gray-700">
            <span className="font-semibold">{TEXT.showing}:</span>
            <span>{activeSummary}</span>
          </div>
        </div>
      </>
    )}
  </div>
);
}

export default AdminHome;

